import { useCallback, useEffect, useMemo, useState } from "react";

import {
    getTransactionPlanFilterOptions,
    getTransactionPlans,
} from "../api/transactionPlanApi";
import { mapSorter } from "../components/transactionPlan/transactionPlanUi";

const DEFAULT_PAGE_SIZE = 20;

/** Loads the paged Гүйлгээний төлөвлөгөө list together with its filter options. */
export default function useTransactionPlanList() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [total, setTotal] = useState(0);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    // Column-header filters. Each entry is the array antd hands back from Table onChange.
    const [columnFilters, setColumnFilters] = useState({});
    const [executionDateRange, setExecutionDateRange] = useState([]);

    const [sortField, setSortField] = useState("executionDate");
    const [sortOrder, setSortOrder] = useState("desc");

    const [filterOptions, setFilterOptions] = useState({
        currencies: [],
        senders: [],
        beneficiaryBanks: [],
        totalCount: 0,
        withInvoiceCount: 0,
    });

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search), 350);
        return () => window.clearTimeout(timer);
    }, [search]);

    /**
     * Identity-stable view of the column filters.
     *
     * antd hands back a fresh filters object on every table event, paging included. Comparing the
     * serialised form keeps paging from being mistaken for a filter change.
     */
    const columnFilterKey = useMemo(() => JSON.stringify(columnFilters), [columnFilters]);

    // A real filter change restarts from the first page; changing page must not.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, columnFilterKey, executionDateRange]);

    const params = useMemo(() => {
        const [from, to] = executionDateRange || [];
        const invoice = columnFilters.hasInvoice;

        return {
            page,
            size: pageSize,
            search: debouncedSearch || undefined,
            currency: columnFilters.currency,
            hasInvoice: Array.isArray(invoice) && invoice.length ? invoice[0] : undefined,
            dateFrom: from ? from.format("YYYY-MM-DD") : undefined,
            dateTo: to ? to.format("YYYY-MM-DD") : undefined,
            sortField,
            sortOrder,
        };
    }, [page, pageSize, debouncedSearch, columnFilters, executionDateRange, sortField, sortOrder]);

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const data = await getTransactionPlans(params);
            setRows(Array.isArray(data?.content) ? data.content : []);
            setTotal(Number(data?.totalElements || 0));
        } catch (err) {
            setRows([]);
            setTotal(0);
            setError(err?.message || "Гүйлгээний төлөвлөгөө ачаалахад алдаа гарлаа");
        } finally {
            setLoading(false);
        }
    }, [params]);

    const loadFilterOptions = useCallback(async () => {
        try {
            const data = await getTransactionPlanFilterOptions();
            setFilterOptions({
                currencies: data?.currencies || [],
                senders: data?.senders || [],
                beneficiaryBanks: data?.beneficiaryBanks || [],
                totalCount: Number(data?.totalCount || 0),
                withInvoiceCount: Number(data?.withInvoiceCount || 0),
            });
        } catch {
            // Filter options are optional; the table stays usable without them.
        }
    }, []);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    const handleTableChange = useCallback((nextPagination, filters, sorter) => {
        if (nextPagination?.current) setPage(nextPagination.current);
        if (nextPagination?.pageSize) setPageSize(nextPagination.pageSize);

        // Keep only the columns that actually have a selection, so an untouched filter does not
        // send an empty array to the API.
        const next = {};
        Object.entries(filters || {}).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length) next[key] = value;
        });

        // Reuse the previous object when nothing actually changed, otherwise the new identity
        // would look like a filter change and reset the page the user just clicked.
        setColumnFilters((prev) =>
            JSON.stringify(prev) === JSON.stringify(next) ? prev : next
        );

        const mapped = mapSorter(sorter);
        setSortField(mapped.sortField || "executionDate");
        setSortOrder(mapped.sortOrder || "desc");
    }, []);

    const clearFilters = useCallback(() => {
        setSearch("");
        setColumnFilters({});
        setExecutionDateRange([]);
        setPage(1);
    }, []);

    const reload = useCallback(() => {
        loadRows();
        loadFilterOptions();
    }, [loadRows, loadFilterOptions]);

    return {
        rows,
        loading,
        error,

        page,
        // Exposed so a page emptied by a bulk delete can fall back to the first one.
        setPage,
        pageSize,
        total,

        search,
        setSearch,
        columnFilters,
        executionDateRange,
        setExecutionDateRange,

        filterOptions,

        handleTableChange,
        clearFilters,
        reload,
    };
}
